import { Icons } from "@/icons/icons"
import { MenuItemModel } from "@syncfusion/ej2-vue-navigations"
import { DownloadContextMenuId, EditContextMenuId, FirmaContextMenuId, SendContextMenuId } from "../types/FileManagerTypes"
import { LexnetContextMenuId } from "@/components/Lexnet/types/LexnetTypes"
import { i18n } from '@/plugins/vue-i18n'
import { PermissionVerbs } from "@/store/modules/auth/authTypes"

export const createEditMenu = (item: MenuItemModel) => {
  item.iconCss = Icons.EDIT
  item.text = i18n.t('components.file_manager.actions.edit.title') as string
  item.id = EditContextMenuId.EDIT
  item.items = [
    {
      text: i18n.t('components.file_manager.actions.edit.cut') as string,
      iconCss: Icons.CUT,
      id: EditContextMenuId.CUT
    },
    {
      text: i18n.t('components.file_manager.actions.edit.copy') as string,
      iconCss: Icons.COPY,
      id: EditContextMenuId.COPY
    },
    {
      text: i18n.t('components.file_manager.actions.edit.paste') as string,
      iconCss: Icons.PASTE,
      id: EditContextMenuId.PASTE
    },
    {
      text: i18n.t('components.file_manager.actions.edit.rename') as string,
      iconCss: Icons.RENAME,
      id: EditContextMenuId.RENAME
    }
  ]
}

export const createEditLayoutMenu = (item: MenuItemModel, isFile: boolean) => {
  item.iconCss = Icons.EDIT
  item.text = i18n.t('components.file_manager.actions.edit.title') as string
  item.id = EditContextMenuId.EDIT
  item.items = [
    {
      text: i18n.t('components.file_manager.actions.edit.paste') as string,
      iconCss: Icons.PASTE,
      id: EditContextMenuId.PASTE
    }
  ]

  if (isFile) {
    item.items.splice(5, 2)
  }
}

export const createEditMultipleMenu = (item: MenuItemModel, isFile: boolean) => {
  item.iconCss = Icons.EDIT
  item.text = i18n.t('components.file_manager.actions.edit.title') as string
  item.id = EditContextMenuId.EDIT
  item.items = [
    {
      text: i18n.t('components.file_manager.actions.edit.cut') as string,
      iconCss: Icons.CUT,
      id: EditContextMenuId.CUT
    },
    {
      text: i18n.t('components.file_manager.actions.edit.copy') as string,
      iconCss: Icons.COPY,
      id: EditContextMenuId.COPY
    }
  ]

  if (isFile) {
    item.items.splice(5, 2)
  }
}

export const createSendMenu = (item: MenuItemModel) => {
  item.iconCss = Icons.SHARE
  item.text = i18n.t('components.file_manager.actions.send.title') as string
  item.id = SendContextMenuId.SEND
  item.items = [
    {
      text: i18n.t('components.file_manager.actions.send.email') as string,
      iconCss: Icons.ATACH,
      id: SendContextMenuId.EMAIL
    },
    {
      text: i18n.t('components.file_manager.actions.send.publish') as string,
      iconCss: Icons.PERIODIC_COM,
      id: SendContextMenuId.PUBLISH
    },
    {
      text: i18n.t('components.file_manager.actions.send.unpublish') as string,
      iconCss: Icons.PERIODIC_COM,
      id: SendContextMenuId.UNPUBLISH
    }
  ]
}

export const createDownloadMenu = (item: MenuItemModel, permissions: PermissionVerbs) => {
  const { canSave } = permissions

  item.iconCss = Icons.DOWNLOAD
  item.text = i18n.t('components.file_manager.actions.context_menu.download') as string
  item.id = DownloadContextMenuId.DOWNLOAD
  item.items = [
    {
      text: i18n.t('components.file_manager.actions.context_menu.download_original') as string,
      iconCss: Icons.DOCUMENTATION,
      id: DownloadContextMenuId.DOWNLOAD_ORIGINAL
    },
    ...(canSave
      ? [
          {
            text: i18n.t('components.file_manager.actions.context_menu.download_as_pdf') as string,
            iconCss: Icons.PDF,
            id: DownloadContextMenuId.DOWNLOAD_AS_PDF
          }
        ]
      : [])
  ].filter(Boolean)
}

export const createFirmaMenu = (item: MenuItemModel) => {
  item.iconCss = Icons.SIGNATURE_CERTIFICATE
  item.text = i18n.t('components.file_manager.actions.firma.signature_and_certification') as string
  item.id = FirmaContextMenuId.FIRMA
  item.items = [
    {
      text: i18n.t('components.file_manager.actions.firma.send_to_firma') as string,
      iconCss: Icons.SIGNATURE,
      id: FirmaContextMenuId.SEND_FIRMA
    },
    {
      text: i18n.t('components.file_manager.actions.firma.certify_document') as string,
      iconCss: Icons.CERTIFIED_DOCUMENT,
      id: FirmaContextMenuId.CERTIFY_DOCUMENT
    },
    {
      text: i18n.t('components.file_manager.actions.firma.send_certified_email') as string,
      iconCss: Icons.CERTIFIED_EMAIL,
      id: FirmaContextMenuId.SEND_CERTIFIED_EMAIL
    },
    {
      text: i18n.t('components.file_manager.actions.firma.send_SMS_email') as string,
      iconCss: Icons.CERTIFIED_SMS,
      id: FirmaContextMenuId.SEND_CERTIFIED_SMS
    },
    {
      text: i18n.t('components.file_manager.actions.firma.monitoring_and_audits') as string,
      iconCss: Icons.REVIEW,
      id: FirmaContextMenuId.MONITORING_REVIEW
    }
  ]
}

export const createLexnetMenu = (item: MenuItemModel) => {
  item.iconCss = Icons.LEXNET
  item.text = i18n.t('components.file_manager.actions.lexnet.title') as string
  item.id = LexnetContextMenuId.LEXNET
  item.items = [
    {
      text: i18n.t('components.file_manager.actions.lexnet.procedure') as string,
      iconCss: Icons.FORMS,
      id: LexnetContextMenuId.PROCEDURE
    },
    {
      text: i18n.t('components.file_manager.actions.lexnet.subject') as string,
      iconCss: Icons.FORMS,
      id: LexnetContextMenuId.SUBJECT
    },
    {
      text: i18n.t('components.file_manager.actions.lexnet.execution') as string,
      iconCss: Icons.FORMS,
      id: LexnetContextMenuId.EXECUTION
    },
    // {
    //   separator: true
    // },
    // {
    //   text: i18n.t('components.file_manager.actions.lexnet.defense') as string,
    //   iconCss: Icons.FORMS,
    //   id: LexnetContextMenuId.DEFENSE
    // },
    {
      text: i18n.t('components.file_manager.actions.lexnet.personation') as string,
      iconCss: Icons.FORMS,
      id: LexnetContextMenuId.PERSONATION
    }
    // {
    //   separator: true
    // },
    // {
    //   text: i18n.t('components.file_manager.actions.lexnet.appeal') as string,
    //   iconCss: Icons.FORMS,
    //   id: LexnetContextMenuId.APPEAL
    // }
  ]
}
